import React from 'react';
import { useTemplates } from '../components/NeuralTemplate/hooks/useTemplates';
import TemplateList from '../components/NeuralTemplate/TemplateList';
import TemplateEditor from '../components/NeuralTemplate/TemplateEditor';

const NeuralTemplatePage: React.FC = () => {
  const {
    listState,
    editorState,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    selectTemplate,
    startNewTemplate,
    cancelEditing,
    markDirty,
    updateFilters,
    loadMore
  } = useTemplates();

  const handleSaveTemplate = async (template: any) => {
    try {
      if (template.id) {
        await updateTemplate(template);
      } else {
        const newTemplate = await createTemplate({
          name: template.name,
          description: template.description,
          voicePrompt: template.voicePrompt,
          elements: template.elements.map(({ id, ...element }) => element),
          category: template.category,
          metadata: template.metadata
        });
        selectTemplate(newTemplate);
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      throw error;
    }
  };

  const handleElementAdd = (element: any) => {
    markDirty();
  };

  const handleElementUpdate = (elementId: string, updates: any) => {
    markDirty();
  };

  const handleElementDelete = (elementId: string) => {
    markDirty();
  };

  const handleElementReorder = (elementId: string, newOrder: number) => {
    markDirty();
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Column - Template List */}
      <div className="w-80 flex-shrink-0">
        <TemplateList
          templates={listState.templates}
          selectedTemplate={editorState.selectedTemplate}
          loading={listState.loading}
          error={listState.error}
          filters={listState.filters}
          onSelectTemplate={selectTemplate}
          onCreateNew={startNewTemplate}
          onFiltersChange={updateFilters}
          onLoadMore={loadMore}
          hasMore={listState.pagination.hasMore}
        />
      </div>

      {/* Middle Column - Template Editor */}
      <div className="flex-1 border-r border-gray-200">
        <TemplateEditor
          template={editorState.selectedTemplate}
          isEditing={editorState.isEditing}
          isDirty={editorState.isDirty}
          onSave={handleSaveTemplate}
          onCancel={cancelEditing}
          onDelete={deleteTemplate}
          onElementAdd={handleElementAdd}
          onElementUpdate={handleElementUpdate}
          onElementDelete={handleElementDelete}
          onElementReorder={handleElementReorder}
        />
      </div>

      {/* Right Column - Reserved for Future Features */}
      <div className="w-80 flex-shrink-0 bg-gray-50 border-l border-gray-200">
        <div className="h-full flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="text-lg font-medium mb-2">Coming Soon</h3>
            <p className="text-sm">
              This space is reserved for future enhancements like:
            </p>
            <ul className="text-xs mt-3 space-y-1">
              <li>• AI-powered suggestions</li>
              <li>• Template analytics</li>
              <li>• Usage insights</li>
              <li>• Related templates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeuralTemplatePage; 